import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { EMPTY, Subject, forkJoin, switchMap, takeUntil } from 'rxjs';
import { StaffService } from '../../../services/staff.service';
import { CostCentersService } from '../../../services/cost-centers.service';
import { AuthService } from '../../../services/auth.service';
import { ErrorService } from '../../../shared/services/error.service';
import { CostCenter } from '../../../models/CostCenter';
import {
  CostCenterBudgetProposal,
  getBudgetProposalStatusLabel
} from '../../../models/CostCenterBudgetProposal';
import { formatBrazilDateTime, toBrazilOffsetIsoString } from '../../../utils/datetime.utils';
import { TableAction, TableColumn } from '../../../shared/components/table/table.component';
import { buildStaffNameMap, exportBudgetProposalsToExcel } from '../../../utils/controladoria-export.util';

interface ProposalRow extends CostCenterBudgetProposal {
  costCenterLabel: string;
  requestedAtLabel: string;
  statusLabel: string;
}

@Component({
  selector: 'app-cost-center-budget-proposals',
  templateUrl: './cost-center-budget-proposals.component.html',
  styleUrls: ['./cost-center-budget-proposals.component.css']
})
export class CostCenterBudgetProposalsComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  rows: ProposalRow[] = [];
  costCenters: CostCenter[] = [];
  isLoading = false;
  isExporting = false;
  isSubmitting = false;
  showCreateModal = false;

  statusFilter = '';
  page = 1;
  pageSize = 10;
  totalItems = 0;
  totalPages = 0;

  tableTitle = 'Propostas de orçamento';
  searchPlaceholder = 'Buscar por centro de custo, status ou justificativa...';

  columns: TableColumn[] = [
    { titulo: '#', label: 'id' },
    { titulo: 'Centro de custo', label: 'costCenterLabel' },
    { titulo: 'Solicitado em', label: 'requestedAtLabel' },
    {
      titulo: 'Status',
      label: 'statusLabel',
      customClass: (_value, row: ProposalRow) => this.statusCellClass(row.status)
    },
    {
      titulo: 'Justificativa',
      label: 'justification',
      formatter: (value: string) => {
        const text = (value || '').trim();
        return text || '—';
      }
    }
  ];

  actions: TableAction[] = [
    {
      label: 'Visualizar',
      icon: 'fas fa-eye',
      action: 'view',
      class: 'btn-view',
      visible: true
    }
  ];

  form = {
    costCenterId: null as number | null,
    proposedMonthlyBudget: null as number | null,
    proposedAnnualBudget: null as number | null,
    justification: ''
  };

  readonly statusOptions = [
    { value: '', label: 'Todos' },
    { value: 'Pending', label: 'Pendente' },
    { value: 'Approved', label: 'Aprovado' },
    { value: 'Rejected', label: 'Rejeitado' },
    { value: 'Cancelled', label: 'Cancelado' }
  ];

  constructor(
    private router: Router,
    private costCentersService: CostCentersService,
    private staffService: StaffService,
    private authService: AuthService,
    private errorService: ErrorService
  ) {}

  viewProposal(id: number): void {
    if (id > 0) {
      this.router.navigate(['/financial/budget-proposals', id, 'view']);
    }
  }

  onRowClick(row: ProposalRow): void {
    if (row?.id) {
      this.viewProposal(row.id);
    }
  }

  onActionClick(event: { action: string; item: ProposalRow }): void {
    if (event.action === 'view' && event.item?.id) {
      this.viewProposal(event.item.id);
    }
  }

  onPageChange(page: number): void {
    this.goToPage(page);
  }

  ngOnInit(): void {
    this.loadCostCenters();
    this.loadProposals();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadCostCenters(): void {
    this.costCentersService
      .getAll({ page: 1, pageSize: 500 })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.costCenters = (res.items || []).slice().sort((a, b) =>
            (a.name || '').localeCompare(b.name || '', 'pt-BR')
          );
        }
      });
  }

  loadProposals(): void {
    this.isLoading = true;
    this.costCentersService
      .getBudgetProposals({
        page: this.page,
        pageSize: this.pageSize,
        status: this.statusFilter || undefined
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.rows = (res.items || []).map((p) => this.toRow(p));
          this.page = res.page;
          this.pageSize = res.pageSize;
          this.totalItems = res.totalItems;
          this.totalPages = res.totalPages;
          this.isLoading = false;
        },
        error: () => {
          this.isLoading = false;
          this.rows = [];
        }
      });
  }

  applyFilters(): void {
    this.page = 1;
    this.loadProposals();
  }

  clearFilters(): void {
    this.statusFilter = '';
    this.applyFilters();
  }

  goToPage(p: number): void {
    if (p < 1 || p > this.totalPages || p === this.page) {
      return;
    }
    this.page = p;
    this.loadProposals();
  }

  openCreateModal(): void {
    this.resetForm();
    this.showCreateModal = true;
  }

  closeCreateModal(): void {
    if (this.isSubmitting) {
      return;
    }
    this.showCreateModal = false;
  }

  onMonthlyBudgetChange(): void {
    const m = Number(this.form.proposedMonthlyBudget);
    if (!Number.isFinite(m) || m < 0) {
      return;
    }
    this.form.proposedAnnualBudget = Math.round(m * 12 * 100) / 100;
  }

  canSubmit(): boolean {
    return (
      this.form.costCenterId != null &&
      this.form.costCenterId > 0 &&
      Number(this.form.proposedMonthlyBudget) > 0 &&
      Number(this.form.proposedAnnualBudget) > 0 &&
      (this.form.justification || '').trim().length >= 3
    );
  }

  submitProposal(): void {
    if (!this.canSubmit() || this.isSubmitting) {
      return;
    }

    this.isSubmitting = true;
    this.authService
      .getStaffId()
      .pipe(
        takeUntil(this.destroy$),
        switchMap((staffId) => {
          if (staffId == null || staffId <= 0) {
            this.isSubmitting = false;
            this.errorService.showErrorMessage(
              'Não foi possível identificar o colaborador logado (token). Faça login novamente.'
            );
            return EMPTY;
          }
          return this.costCentersService.createBudgetProposal({
            costCenterId: Number(this.form.costCenterId),
            proposedMonthlyBudget: Number(this.form.proposedMonthlyBudget),
            proposedAnnualBudget: Number(this.form.proposedAnnualBudget),
            justification: this.form.justification.trim(),
            requestedByStaffId: staffId,
            requestedAt: toBrazilOffsetIsoString()
          });
        })
      )
      .subscribe({
        next: () => {
          this.isSubmitting = false;
          this.showCreateModal = false;
          this.errorService.showSuccessMessage('Proposta de orçamento enviada com sucesso.');
          this.loadProposals();
        },
        error: () => {
          this.isSubmitting = false;
        }
      });
  }

  private resetForm(): void {
    this.form = {
      costCenterId: null,
      proposedMonthlyBudget: null,
      proposedAnnualBudget: null,
      justification: ''
    };
  }

  private toRow(p: CostCenterBudgetProposal): ProposalRow {
    return {
      ...p,
      costCenterLabel: this.formatCostCenterLabel(p),
      requestedAtLabel: this.formatDateTime(p.requestedAt),
      statusLabel: this.statusLabel(p.status)
    };
  }

  /** Nome/código vindos da API (`costCenterName` / `costCenterCode`); fallback na lista local do cadastro. */
  private formatCostCenterLabel(p: CostCenterBudgetProposal): string {
    const apiName = (p.costCenterName || '').trim();
    const apiCode = (p.costCenterCode || '').trim();
    if (apiName && apiCode) {
      return `${apiCode} — ${apiName}`;
    }
    if (apiName) {
      return apiName;
    }
    if (apiCode) {
      return apiCode;
    }
    const cc = this.costCenters.find((c) => c.id === p.costCenterId);
    if (cc) {
      const code = (cc.code || '').trim();
      const name = (cc.name || '').trim();
      if (code && name) {
        return `${code} — ${name}`;
      }
      return name || code;
    }
    return '—';
  }

  /** Exporta todas as propostas (filtro atual), com visão completa dos campos da API. */
  exportToExcel(): void {
    if (this.isExporting) return;
    this.isExporting = true;

    forkJoin({
      proposals: this.costCentersService.getBudgetProposals({
        page: 1,
        pageSize: Math.max(this.totalItems, 1) || 500,
        status: this.statusFilter || undefined
      }),
      staff: this.staffService.getStaff()
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: ({ proposals, staff }) => {
          const items = proposals.items || [];
          if (items.length === 0) {
            this.errorService.showErrorMessage('Não há propostas para exportar.');
          } else {
            const staffNames = buildStaffNameMap(staff || []);
            exportBudgetProposalsToExcel(items, staffNames);
            this.errorService.showSuccessMessage(`Exportadas ${items.length} proposta(s).`);
          }
          this.isExporting = false;
        },
        error: () => {
          this.isExporting = false;
        }
      });
  }

  private statusLabel(status: string): string {
    return getBudgetProposalStatusLabel(status);
  }

  private statusCellClass(status: string): string {
    const base = 'bp-status-cell';
    switch ((status || '').trim()) {
      case 'Pending':
        return `${base} bp-status-cell--pending`;
      case 'Approved':
        return `${base} bp-status-cell--approved`;
      case 'Rejected':
        return `${base} bp-status-cell--rejected`;
      case 'Cancelled':
        return `${base} bp-status-cell--cancelled`;
      default:
        return `${base} bp-status-cell--muted`;
    }
  }

  private formatDateTime(value?: string): string {
    return formatBrazilDateTime(value);
  }
}
